import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  useGetDrawerNotificationsQuery,
  useGetDrawerFiltersQuery,
  useDismissNotificationMutation,
  useDismissAllNotificationsMutation,
  useLazyGetNotificationDetailsQuery,
} from "Services/Notifications/Notifications";
import { useNewArrivalIds } from "Common/Hooks";
import { FILTER_OPTION_TYPE } from "Common/Constants";
import type { IDatePresetRangeFilterValue, IFilterOption, INotification } from "Common/Interfaces";
import { getFilterState, formatRelativeTime } from "../Functions";

interface IUseNotificationDrawer {
  open: boolean;
  onClose: () => void;
  sessionStartTime: string;
  pendingSignalRItems: INotification[];
  onPendingItemsConsumed: () => void;
  onDismissed: () => void;
  onMarkedUnread: () => void;
  onAllDismissed: () => void;
}

const PAGE_LIMIT = 20;

const PRESET_MINUTES: Record<string, number> = {
  "30min": 30,
  "1hour": 60,
  "12hour": 720,
  "24hour": 1440,
};

const computePresetDates = (preset: IDatePresetRangeFilterValue): { from: dayjs.Dayjs | null; to: dayjs.Dayjs | null } => {
  const { selectedPreset, customStartDate, customEndDate } = preset;
  if (!selectedPreset) return { from: null, to: null };
  if (selectedPreset === "custom") return { from: customStartDate, to: customEndDate };
  const minutes = PRESET_MINUTES[selectedPreset];
  return { from: minutes ? dayjs().subtract(minutes, "minute") : null, to: null };
};

const getNotificationId = (notification: INotification) => notification.id;

const getIsReadFromFilterValue = (value: string | undefined): boolean | undefined => {
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
};

export const useNotificationDrawer = (props: IUseNotificationDrawer) => {
  const {
    open,
    onClose,
    sessionStartTime,
    pendingSignalRItems,
    onPendingItemsConsumed,
    onDismissed,
    onMarkedUnread,
    onAllDismissed,
  } = props;

  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<INotification[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [checkboxFilterValues, setCheckboxFilterValues] = useState<Record<string, string | undefined>>({});
  const [datePreset, setDatePreset] = useState<IDatePresetRangeFilterValue>({
    selectedPreset: null,
    customStartDate: null,
    customEndDate: null,
  });
  const [createdDateFrom, setCreatedDateFrom] = useState<dayjs.Dayjs | null>(null);
  const [createdDateTo, setCreatedDateTo] = useState<dayjs.Dayjs | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const isFiltering = Boolean(search || Object.values(checkboxFilterValues).some(Boolean) || datePreset.selectedPreset);

  const queryArgs = open
    ? {
      offset,
      limit: PAGE_LIMIT,
      search: search || undefined,
      applicationIds: checkboxFilterValues["applicationIds"],
      sessionStartTime,
      isRead: getIsReadFromFilterValue(checkboxFilterValues["isRead"]),
      createdDateFrom: createdDateFrom || undefined,
      createdDateTo: createdDateTo || undefined,
    }
    : skipToken;

  const { data, isFetching } = useGetDrawerNotificationsQuery(queryArgs);
  const { data: drawerFilters } = useGetDrawerFiltersQuery(open ? { sessionStartTime } : skipToken);

  const [dismissNotification] = useDismissNotificationMutation();
  const [dismissAllNotifications] = useDismissAllNotificationsMutation();
  const [fetchNotificationDetails] = useLazyGetNotificationDetailsQuery();

  const [selectedNotification, setSelectedNotification] = useState<INotification | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [readPendingNotificationIds, setReadPendingNotificationIds] = useState<Set<string>>(() => new Set());
  const [dismissedPendingNotificationIds, setDismissedPendingNotificationIds] = useState<Set<string>>(() => new Set());

  const [exitingIds, setExitingIds] = useState<Set<string>>(() => new Set());

  const newlyArrivedIds = useNewArrivalIds({
    open,
    items: pendingSignalRItems,
    getId: getNotificationId,
  });

  const hasMore = totalCount > allItems.length;

  const filterState = useMemo(
    () =>
      getFilterState({
        filters: drawerFilters,
        filterValues: checkboxFilterValues,
        datePreset,
      }),
    [drawerFilters, checkboxFilterValues, datePreset],
  );

  // Consume pending SignalR items when drawer first opens
  useEffect(() => {
    if (open) {
      onPendingItemsConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Accumulate pages from API; clear SignalR-prepended items on any fresh load (offset=0)
  useEffect(() => {
    if (!open || !data) return;
    setIsDataLoaded(true);
    setTotalCount(data.totalCount);
    if (offset === 0) {
      onPendingItemsConsumed();
      setAllItems(data.data);
    } else {
      setAllItems((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const newItems = data.data.filter((n: INotification) => !existingIds.has(n.id));
        return [...prev, ...newItems];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, offset, open]);

  const filteredPendingSignalRItems = useMemo(() => {
    const selectedApplicationIds = new Set(checkboxFilterValues["applicationIds"]?.split(",").filter(Boolean));
    const isRead = getIsReadFromFilterValue(checkboxFilterValues["isRead"]);
    const normalizedSearch = search.trim().toLowerCase();

    return pendingSignalRItems.filter((notification) => {
      if (selectedApplicationIds.size > 0 && !selectedApplicationIds.has(notification.applicationId?.toString() ?? "")) {
        return false;
      }

      if (isRead !== undefined && notification.isRead !== isRead) return false;

      const createdDay = dayjs(notification.createdAt).startOf("day");
      if (createdDateFrom && createdDay.isBefore(createdDateFrom.startOf("day"))) return false;
      if (createdDateTo && createdDay.isAfter(createdDateTo.startOf("day"))) return false;

      if (!normalizedSearch) return true;

      return [notification.title, notification.message, notification.applicationName].some((value) =>
        value?.toLowerCase().includes(normalizedSearch),
      );
    });
  }, [checkboxFilterValues, createdDateFrom, createdDateTo, pendingSignalRItems, search]);

  // Merge matching pending SignalR items at the top
  const displayedItems = useMemo(() => {
    if (filteredPendingSignalRItems.length === 0) return allItems;
    const apiIds = new Set(allItems.map((n) => n.id));
    const newItems = filteredPendingSignalRItems
      .filter((n) => !apiIds.has(n.id) && !dismissedPendingNotificationIds.has(n.id))
      .map((notification) =>
        readPendingNotificationIds.has(notification.id) ? { ...notification, isRead: true } : notification,
      );
    return newItems.length ? [...newItems, ...allItems] : allItems;
  }, [allItems, dismissedPendingNotificationIds, filteredPendingSignalRItems, readPendingNotificationIds]);

  const resetDrawerState = () => {
    setSearch("");
    setCheckboxFilterValues({});
    setDatePreset({ selectedPreset: null, customStartDate: null, customEndDate: null });
    setCreatedDateFrom(null);
    setCreatedDateTo(null);
    setOffset(0);
    setAllItems([]);
    setTotalCount(0);
    setReadPendingNotificationIds(new Set());
    setDismissedPendingNotificationIds(new Set());
    setExitingIds(new Set());
    setIsDetailOpen(false);
    setIsDataLoaded(false);
  };

  const handleClose = () => {
    resetDrawerState();
    onClose();
  };

  useEffect(() => {
    if (!open) resetDrawerState();
  }, [open]);

  const handleLoadMore = () => {
    if (!isFetching && hasMore) {
      setOffset((prev) => prev + PAGE_LIMIT);
    }
  };

  const handleSearch = (searchText: string) => {
    setSearch(searchText);
    setOffset(0);
    setTotalCount(0);
  };

  const handleClearSearch = () => {
    setSearch("");
    setOffset(0);
    setTotalCount(0);
  };

  const handleUpdateFilterState = (updatedFilterState: IFilterOption[]) => {
    const newFilterValues: Record<string, string | undefined> = {};

    updatedFilterState
      .filter((f) => f.type === FILTER_OPTION_TYPE.CHECKBOX)
      .forEach((filter) => {
        const drawerFilter = drawerFilters?.find((df) => df.id === filter.id);
        if (!drawerFilter?.queryParameter) return;
        const checked = filter.checkboxValues
          ?.filter((v) => v.isChecked)
          .map((v) => v.value)
          .join(",");
        newFilterValues[drawerFilter.queryParameter] = checked || undefined;
      });

    const dateFilter = updatedFilterState.find((f) => f.label.toLowerCase() === "date & time");
    let newDatePreset: IDatePresetRangeFilterValue = dateFilter?.datePresetRangeValues ?? {
      selectedPreset: null,
      customStartDate: null,
      customEndDate: null,
    };
    if (newDatePreset.selectedPreset === "custom" && !newDatePreset.customStartDate && !newDatePreset.customEndDate) {
      newDatePreset = { selectedPreset: null, customStartDate: null, customEndDate: null };
    }
    const { from: newDateFrom, to: newDateTo } = computePresetDates(newDatePreset);

    const effectiveNewIsRead = getIsReadFromFilterValue(newFilterValues["isRead"]);
    const effectiveCurrentIsRead = getIsReadFromFilterValue(checkboxFilterValues["isRead"]);

    const dateChanged =
      newDatePreset.selectedPreset !== datePreset.selectedPreset ||
      (newDatePreset.selectedPreset === "custom" &&
        (newDatePreset.customStartDate !== datePreset.customStartDate ||
          newDatePreset.customEndDate !== datePreset.customEndDate));

    const queryWillChange =
      effectiveNewIsRead !== effectiveCurrentIsRead ||
      newFilterValues["applicationIds"] !== checkboxFilterValues["applicationIds"] ||
      dateChanged;

    setCheckboxFilterValues(newFilterValues);
    setDatePreset(newDatePreset);
    setCreatedDateFrom(newDateFrom);
    setCreatedDateTo(newDateTo);
    setOffset(0);

    if (queryWillChange) {
      setAllItems([]);
      setTotalCount(0);
      setIsDataLoaded(false);
    }
  };

  const handleMarkAsReadFailure = (notification: INotification, openDetail: boolean) => {
    if (openDetail) {
      setSelectedNotification(notification);
      setIsDetailOpen(true);
    }
    setAllItems((prev) => prev.map((n) => (n.id === notification.id ? notification : n)));
    setReadPendingNotificationIds((prev) => {
      const next = new Set(prev);
      next.delete(notification.id);
      return next;
    });
    onMarkedUnread();
  };

  const handleNotificationClick = (notification: INotification, openDetail = true) => {
    if (notification.isRead) {
      if (openDetail) {
        setSelectedNotification(notification);
        setIsDetailOpen(true);
      }
    } else {
      const readNotification = { ...notification, isRead: true };
      if (openDetail) {
        setSelectedNotification(readNotification);
        setIsDetailOpen(true);
      }
      setAllItems((prev) => prev.map((n) => (n.id === notification.id ? readNotification : n)));
      setReadPendingNotificationIds((prev) => new Set(prev).add(notification.id));
      onDismissed();
    }

    fetchNotificationDetails(Number(notification.id))
      .unwrap()
      .then((details) => {
        if (openDetail) setSelectedNotification({ ...details, isRead: true });
      })
      .catch(() => {
        if (notification.isRead) return;
        handleMarkAsReadFailure(notification, openDetail);
      });
  };

  const handleDetailClose = () => {
    setIsDetailOpen(false);
  };

  const handleDismiss = (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    const notification = displayedItems.find((n) => n.id === notificationId);

    // Start exit animation immediately
    setExitingIds((prev) => new Set(prev).add(notificationId));

    dismissNotification(notificationId)
      .unwrap()
      .then(() => {
        // Actual removal handled by handleRowExited after collapse finishes
      })
      .catch(() => {
        // Failure: cancel the exit animation (row reappears)
        setExitingIds((prev) => {
          const next = new Set(prev);
          next.delete(notificationId);
          return next;
        });
      });

    if (notification && !notification.isRead) {
      onDismissed();
    }
  };

  const handleDismissAll = () => {
    // Gather all currently displayed notification ids
    const allDisplayedIds = [
      ...allItems.map((n) => n.id),
      ...filteredPendingSignalRItems.filter((n) => !dismissedPendingNotificationIds.has(n.id)).map((n) => n.id),
    ];

    // Start exit animation on all visible rows simultaneously
    setExitingIds((prev) => {
      const next = new Set(prev);
      allDisplayedIds.forEach((id) => next.add(id));
      return next;
    });

    dismissAllNotifications()
      .unwrap()
      .then(() => {
        // Actual removal handled by handleRowExited per-row after each collapse finishes
      })
      .catch(() => {
        // Failure: cancel all exit animations (rows reappear)
        setExitingIds(new Set());
      });

    onAllDismissed();
  };

  const handleRowExited = (notificationId: string) => {
    // Remove from allItems (covers API-fetched items)
    setAllItems((prev) => prev.filter((n) => n.id !== notificationId));
    // Add to dismissedPendingNotificationIds (covers pending SignalR items)
    setDismissedPendingNotificationIds((prev) => new Set(prev).add(notificationId));
    // Clean up exiting tracking
    setExitingIds((prev) => {
      const next = new Set(prev);
      next.delete(notificationId);
      return next;
    });
  };

  return {
    isDataLoaded,
    newlyArrivedIds,
    exitingIds,
    handleRowExited,
    allItems: displayedItems,
    isFetching,
    totalCount,
    hasMore,
    search,
    isFiltering,
    selectedNotification,
    isDetailOpen,
    filterState,
    formatRelativeTime,
    handleClose,
    handleSearch,
    handleClearSearch,
    handleUpdateFilterState,
    handleNotificationClick,
    handleDismiss,
    handleDismissAll,
    handleDetailClose,
    handleLoadMore,
  };
};
